import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Clock } from "lucide-react";

function isoIn(hours) {
  const d = new Date(Date.now() + hours * 3600 * 1000);
  return d.toISOString();
}

function isoTomorrow9() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

function isoNextMondayMorning() {
  const d = new Date();
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const daysUntilMon = (8 - day) % 7 || 7;
  d.setDate(d.getDate() + daysUntilMon);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

function fmt(iso) {
  return new Date(iso).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function toDatetimeLocal(iso) {
  // Convert ISO to "YYYY-MM-DDTHH:mm" in local TZ
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SnoozeDialog({ open, onClose, onConfirm, lang = "de" }) {
  const [custom, setCustom] = useState(toDatetimeLocal(isoIn(2)));

  const quickOptions = [
    { key: "1h", labelDe: "1 Stunde", labelEn: "1 hour", value: () => isoIn(1) },
    { key: "3h", labelDe: "3 Stunden", labelEn: "3 hours", value: () => isoIn(3) },
    { key: "tomorrow", labelDe: "Morgen 09:00", labelEn: "Tomorrow 9 AM", value: isoTomorrow9 },
    { key: "next_week", labelDe: "Nächste Woche", labelEn: "Next week", value: isoNextMondayMorning },
  ];

  const apply = (iso) => {
    onConfirm(iso);
  };

  const applyCustom = () => {
    if (!custom) return;
    const iso = new Date(custom).toISOString();
    if (new Date(iso).getTime() <= Date.now()) {
      alert(lang === "de" ? "Bitte einen Zeitpunkt in der Zukunft wählen." : "Pick a time in the future.");
      return;
    }
    apply(iso);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="snooze-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="w-4 h-4" /> {lang === "de" ? "Später erinnern" : "Snooze conversation"}</DialogTitle>
          <DialogDescription>
            {lang === "de" ? 'Die Konversation wird aus "Offen" verschoben und automatisch zur gewählten Zeit wieder geöffnet.' : 'Conversation is moved out of "Open" and will reappear at the chosen time.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-neutral-600 mb-2">{lang === "de" ? "Schnellauswahl" : "Quick options"}</p>
            <div className="grid grid-cols-2 gap-2">
              {quickOptions.map((opt) => {
                const iso = opt.value();
                return (
                  <Button
                    key={opt.key}
                    onClick={() => apply(iso)}
                    variant="outline"
                    className="justify-start flex-col items-start h-auto py-2"
                    data-testid={`snooze-quick-${opt.key}`}
                  >
                    <span className="text-sm font-semibold">{lang === "de" ? opt.labelDe : opt.labelEn}</span>
                    <span className="text-[10px] text-neutral-500">{fmt(iso)}</span>
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-neutral-600 mb-2">{lang === "de" ? "Eigener Zeitpunkt" : "Custom time"}</p>
            <div className="flex gap-2">
              <Input
                type="datetime-local"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                data-testid="snooze-custom-input"
                className="flex-1"
              />
              <Button onClick={applyCustom} data-testid="snooze-custom-apply">{lang === "de" ? "Anwenden" : "Apply"}</Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="snooze-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
